// Post-sign-in return path for shared links (architecture sprint-05 §6.6). localStorage, so it survives
// the portal SSO round trip even in a new tab. Only '/shared/<token>' paths are ever stored or
// followed (no open redirect). Every storage access is guarded: failure means no return-to.

const KEY = 'tt-return-to';
const TTL_MS = 30 * 60 * 1000;
const ALLOWED = /^\/shared\/[A-Za-z0-9_-]{1,128}$/;

interface Stored {
  path: string;
  savedAt: number;
}

function isStored(value: unknown): value is Stored {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<Stored>;
  return typeof v.path === 'string' && typeof v.savedAt === 'number';
}

export function isReturnToPath(path: string): boolean {
  return ALLOWED.test(path);
}

/** Stores { path, savedAt } only for an allowed path. */
export function saveReturnTo(path: string, nowMs = Date.now()): void {
  if (!isReturnToPath(path)) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ path, savedAt: nowMs }));
  } catch {
    // Storage blocked: the visitor lands on '/' after sign-in instead.
  }
}

export function clearReturnTo(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** A valid, fresh path, else null (a stale or invalid entry is removed). */
export function peekReturnTo(nowMs = Date.now()): string | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (!isStored(parsed) || !isReturnToPath(parsed.path) || nowMs - parsed.savedAt > TTL_MS || nowMs < parsed.savedAt - TTL_MS) {
    clearReturnTo();
    return null;
  }
  return parsed.path;
}

/** peek + remove. */
export function takeReturnTo(nowMs = Date.now()): string | null {
  const path = peekReturnTo(nowMs);
  if (path !== null) clearReturnTo();
  return path;
}
